import { useMemo, useState, useEffect } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Chip,
    Button,
    CircularProgress,
    Collapse,
    Typography
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { useMapData } from '../../hooks/map/useMapData';
import { useMapStore } from '../../stores/map/useMapStore';
import { useVehiclesStore } from '../../stores/useVehiclesStore';
import CustomSelect from '../CustomSelect';
import { formatDateTime } from '../../utils/dateUtils';
import { useMapActions } from '../../hooks/map/useMapActions';

export default function MapPezinhoVehicles() {

    const {
        focusPezinhoPosition,
        focusPoint
    } = useMapActions();

    const [
        expandedVehicles,
        setExpandedVehicles
    ] = useState([]);

    const [
        expanded,
        setExpanded
    ] = useState(false);

    const {
        pezinho,
        selectedPoint
    } = useMapData();

    const {
        vehicles,
        selectedVehicles,
        selectedPositions,
        loading,
        loaded
    } = pezinho;

    const tripData = useMapStore(
        state => state.tripData
    );

    const tripPlate =
        String(
            tripData?.placa || ''
        ).trim();

    const veiculosDisponiveis =
        useVehiclesStore(
            state => state.veiculosDisponiveis
        );

    const vehicleItems = useMemo(() => {

        const vehiclesByPlate =
            new Map(
                (veiculosDisponiveis || [])
                    .map(vehicle => [
                        String(vehicle.id).trim(),
                        vehicle.label
                    ])
            );

        return vehicles
            .sort((a, b) =>
                (b.positions?.length || 0) -
                (a.positions?.length || 0)
            )
            .map(vehicle => {

                const placa =
                    String(
                        vehicle.placa
                    ).trim();

                const positionsCount =
                    vehicle.positions?.length || 0;

                return {
                    id: placa,

                    label:
                        vehiclesByPlate.get(placa) ||
                        placa,

                    secondaryText:
                        `${positionsCount} ${
                            positionsCount === 1
                                ? 'posição'
                                : 'posições'
                        }`
                };

            });

    }, [
        vehicles,
        veiculosDisponiveis
    ]);

    const visibleVehicles = useMemo(() => {

        const selectedSet =
            new Set(
                selectedVehicles || []
            );

        const labelsByPlate =
            new Map(
                vehicleItems.map(item => [
                    item.id,
                    item.label
                ])
            );

        return vehicles
            .filter(vehicle => {

                const placa =
                    String(
                        vehicle?.placa || ''
                    ).trim();

                return selectedSet.has(placa);

            })
            .sort((a, b) =>
                (b.positions?.length || 0) -
                (a.positions?.length || 0)
            )
            .map(vehicle => {

                const placa =
                    String(
                        vehicle.placa
                    ).trim();

                return {
                    ...vehicle,
                    placa,
                    label:
                        labelsByPlate.get(placa) ||
                        placa,
                    isTripVehicle:
                        !!tripPlate &&
                        placa === tripPlate
                };

            });

    }, [
        tripPlate,
        vehicles,
        vehicleItems,
        selectedVehicles
    ]);

    useEffect(() => {

        if (!vehicleItems.length) {
            setExpanded(false);
        }

    }, [vehicleItems.length]);

    const setPezinhoSelectedVehicles =
        useMapStore(
            state =>
                state.setPezinhoSelectedVehicles
        );

    const setPezinhoSelectedPosition =
        useMapStore(
            state =>
                state.setPezinhoSelectedPosition
        );

    const toggleVehiclePositions = (placa) => {

        setExpandedVehicles(current => {

            if (current.includes(placa)) {

                return current.filter(
                    item => item !== placa
                );
            }

            return [
                ...current,
                placa
            ];

        });

    };

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, isExpanded) => {

                if (!vehicleItems.length) {
                    return;
                }

                setExpanded(isExpanded);

            }}
            disableGutters
            elevation={2}
            sx={{
                width: '100%',
                boxSizing: 'border-box',

                '&:before': {
                    display: 'none'
                }
            }}
        >
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon />
                }
                sx={{
                    minHeight: 40,

                    '& .MuiAccordionSummary-content': {
                        my: 0.5
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    {loading ? (
                        <CircularProgress
                            size={18}
                        />
                    ) : (
                        <DirectionsBusIcon
                            fontSize="small"
                        />
                    )}

                    <Typography
                        variant="body2"
                        fontWeight={500}
                    >
                        {loading
                            ? 'Buscando veículos...'
                            : !loaded
                                ? 'Veículos próximos'
                                : vehicles.length > 0
                                    ? 'Veículos encontrados'
                                    : 'Nenhum veículo encontrado'}
                    </Typography>

                    {!loading &&
                        !!vehicles.length && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                ({vehicles.length})
                            </Typography>
                        )}
                </Box>
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    pt: 0,
                    px: 1.5,
                    pb: 1.5
                }}
            >
                {!!vehicleItems.length ? (
                    <>
                        <CustomSelect
                            items={vehicleItems}
                            selected={
                                selectedVehicles
                            }
                            onChange={
                                setPezinhoSelectedVehicles
                            }
                            label="Veículos"
                            allowNone={true}
                            allowAll={true}
                            multiple
                            maxVisibleLabels={1}
                            enableSearch={true}
                            enabledToggle={true}
                            layout="compact"
                            disabled={loading}
                        />

                        <Box
                            sx={{
                                mt: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75,
                                maxHeight: 'calc(100dvh - 180px)',
                                overflowY: 'auto'
                            }}
                        >
                            {visibleVehicles.map(vehicle => {

                                const hasSelectedPosition =
                                    selectedPositions?.some(
                                        position =>
                                            String(
                                                position?.placa || ''
                                            ).trim() === vehicle.placa
                                    );

                                return (
                                    <Box
                                        key={vehicle.placa}
                                        sx={(theme) => ({
                                            px: 1,
                                            py: 0.75,

                                            borderRadius: 1,

                                            border: `1px solid ${
                                                hasSelectedPosition
                                                    ? theme.palette.primary.main
                                                    : theme.palette.divider
                                            }`,

                                            backgroundColor:
                                                hasSelectedPosition
                                                    ? theme.palette.action.selected
                                                    : theme.palette.background.default
                                        })}
                                    >
                                    {/* VEÍCULO */}

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: 0
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexDirection: 'column',
                                                    gap: 0.2,
                                                    minWidth: 0
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={500}
                                                    noWrap
                                                    sx={{
                                                        minWidth: 0
                                                    }}
                                                >
                                                    {vehicle.label}
                                                </Typography>

                                                {vehicle.isTripVehicle && (
                                                    <Chip
                                                        label="Veículo da viagem"
                                                        size="small"
                                                        color="warning"
                                                        variant="contained"
                                                        sx={{
                                                            height: 18,
                                                            flexShrink: 0,

                                                            '& .MuiChip-label': {
                                                                px: 0.75,
                                                                fontSize: '0.65rem'
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() =>
                                                toggleVehiclePositions(
                                                    vehicle.placa
                                                )
                                            }
                                            endIcon={
                                                expandedVehicles.includes(
                                                    vehicle.placa
                                                )
                                                    ? <KeyboardArrowUpIcon />
                                                    : <KeyboardArrowDownIcon />
                                            }
                                            sx={{
                                                minWidth: 0,
                                                px: 0.5,
                                                py: 0,
                                                whiteSpace: 'nowrap',
                                                textTransform: 'none',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {vehicle.positions?.length || 0}{' '}
                                            {vehicle.positions?.length > 1 ? 'posições' : 'posição'}
                                        </Button>
                                    </Box>

                                    {/* DADOS */}

                                    <Box
                                        sx={{
                                            mt: 0.5,

                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            columnGap: 1.5,
                                            rowGap: 0.25
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Primeira:{' '}
                                            {formatDateTime(
                                                vehicle.firstAt,
                                                true
                                            )}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Última:{' '}
                                            {formatDateTime(
                                                vehicle.lastAt,
                                                true
                                            )}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Aproximação:{' '}
                                            {Math.round(
                                                vehicle.minDistance
                                            )} m
                                        </Typography>
                                    </Box>

                                    <Collapse
                                        in={
                                            expandedVehicles.includes(
                                                vehicle.placa
                                            )
                                        }
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        <Box
                                            sx={{
                                                mt: 0.75,
                                                pt: 0.75,
                                                borderTop: 1,
                                                borderColor: 'divider',

                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5
                                            }}
                                        >
                                            {vehicle.positions?.map(
                                                (position) => {

                                                    const isSelected =
                                                        selectedPositions?.some(
                                                            selected =>
                                                                selected.POSITION_UID ===
                                                                position.POSITION_UID
                                                        );

                                                    return (
                                                        <Box
                                                            key={position.POSITION_UID}
                                                            onClick={() => {

                                                                setPezinhoSelectedPosition(
                                                                    position
                                                                );

                                                                // =========================
                                                                // 0 → 1
                                                                // =========================

                                                                if (
                                                                    !isSelected &&
                                                                    selectedPositions.length === 0
                                                                ) {

                                                                    focusPezinhoPosition(
                                                                        position
                                                                    );

                                                                    return;
                                                                }

                                                                // =========================
                                                                // 1 → 0
                                                                // =========================

                                                                if (
                                                                    isSelected &&
                                                                    selectedPositions.length === 1
                                                                ) {

                                                                    focusPoint(
                                                                        selectedPoint
                                                                    );

                                                                }

                                                            }}
                                                            sx={(theme) => ({
                                                                px: 0.75,
                                                                py: 0.5,

                                                                borderRadius: 1,

                                                                cursor: 'pointer',

                                                                backgroundColor:
                                                                    isSelected
                                                                        ? theme.palette.action.selected
                                                                        : 'transparent',

                                                                '&:hover': {
                                                                    backgroundColor:
                                                                        isSelected
                                                                            ? theme.palette.action.selected
                                                                            : theme.palette.action.hover
                                                                }
                                                            })}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color={
                                                                    isSelected
                                                                        ? 'primary'
                                                                        : 'text.primary'
                                                                }
                                                            >
                                                                {formatDateTime(
                                                                    position.dataHora,
                                                                    true
                                                                )}
                                                            </Typography>

                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    ml: 1
                                                                }}
                                                            >
                                                                {Math.round(
                                                                    position.distance
                                                                )} m
                                                            </Typography>
                                                        </Box>
                                                    );
                                                }
                                            )}
                                        </Box>
                                    </Collapse>
                                </Box>
                                );

                            })}

                            {!visibleVehicles.length && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Nenhum veículo selecionado.
                                </Typography>
                            )}
                        </Box>
                    </>
                ) : (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        {loading
                            ? 'Aguarde a busca...'
                            : loaded
                                ? 'Nenhum veículo encontrado.'
                                : 'Selecione um ponto para buscar veículos próximos.'}
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    );
}